import {FeatureCollection, Feature, Point} from "geojson";
import {Layer, MarkerProps, Source, SourceProps, MarkerEvent, MapRef} from "react-map-gl/maplibre";
import MapClusterIcon, {IMapClusterIconProps, MapClusterIconInstance} from "./MapClusterIcon";
import React, {RefObject} from "react";
import {CameraOptions, FlyToOptions, JumpToOptions, LngLat, LngLatBounds} from "maplibre-gl";
import {MapLibreMapInstance, MapFocus} from "../MapLibreMap";
import MapMarker, {IMarkerProps, IMarkersProps, MapMarkerInstance} from "./MapMarker";
import {CircleLayerSpecification, GeoJSONSourceSpecification} from "@maplibre/maplibre-gl-style-spec";
import {IAnyObject, INumberObject, IStringArrayObject, IStringObject} from "../../../ts/common/SimpleInterface";
import DeferredPromise, {createDeferredPromise} from "../../../ts/common/DeferredPromise";
import {mapCoreConfig} from "../config/MapCoreConfig";
import {Property} from "csstype";
import {deepMergeObjects} from "../../../ts/common/SiteHelper";
import BasicComponent from "../../../tsx/component/BasicComponent";
import {getManager} from "../../../ts/manager/Manager";

interface IClusterObject
{
    clusterID:string;
    feature:Feature|null;
    lngLat:LngLat;
    mapMarkerRef: RefObject<MapMarkerInstance|null>;
    active:boolean;
    fromMarkerProps:IMarkerProps|null;
    markerProps:IMarkerProps;
    itemCount:number;
    markerID?:string|null;
    objectID?:string|null
}

interface IClusterObjects
{
    [key: string]: IClusterObject;
}

export interface IMapClusterProps
{
    markers?:IMarkersProps
    clusterIcon?:IMapClusterIconProps;
    withCountry?:boolean
    withUser?:boolean|string;
    withPrimaryMarker?:boolean;
    zIndex?:number
    buttonZIndex?:number
    layer?:CircleLayerSpecification
    source?:GeoJSONSourceSpecification & SourceProps
    marker?:IMarkerProps|null
    sortByNorthWest?:boolean
    transitionOnClusterChange?:boolean
}

interface IProps extends IMapClusterProps
{
    map:MapLibreMapInstance
}

export class MapClusterInstance extends BasicComponent<IProps>
{
    props: IProps;
    clusterObjects:IClusterObjects = {}
    sourceID:string = "";
    appliedToMap = false;
    sourceProps:GeoJSONSourceSpecification & SourceProps|null = null;
    markersProps:IMarkersProps = null!;
    clusterIconPropsDidChange = false;
    willRenderContent = false;
    excludeFromClusterMarkerIDs:string[] = [];
    excludeFromClusterObjectIDs:IStringArrayObject = {};
    nextUpdatePromises:DeferredPromise<void, void>[] = [];
    updateIsRequired:boolean = false;
    forceRemoveClusters:boolean = false;
    waitingForSourceData = false;
    refreshIndex = 0;
    markerPropsList:IMarkerProps[] = [];

    constructor(props: IProps)
    {
        super(props);
        this.props = props;
    }

    applyNextProps(nextProps:IProps, props?:IProps)
    {
        super.applyNextProps(nextProps, props);

        const markersProps = nextProps.markers ?? {};
        let haveMarkers = !!Object.keys(markersProps).length;
        let sourcePropsDidChange = haveMarkers;
        let markersPropsDidChange = haveMarkers;
        let clusterIconPropsDidChange = haveMarkers;
        let clusterDataDidChange = haveMarkers;

        const sortByNorthWest = nextProps.sortByNorthWest ?? (mapCoreConfig.cluster?.sortByNorthWest ?? false);
        this.markerPropsList = this.createMarkerPropsList(markersProps, sortByNorthWest)
        const features = this.createClusterFeatures(this.markerPropsList, this.excludeFromClusterMarkerIDs, this.excludeFromClusterObjectIDs);
        this.checkForTransition(nextProps, props)

        if(props){
            sourcePropsDidChange = (props.layer !== nextProps.layer) || (props.source !== nextProps.source);
            markersPropsDidChange = props.markers !== nextProps.markers;
            clusterIconPropsDidChange = props.clusterIcon !== nextProps.clusterIcon;
            const currentClusterData:FeatureCollection|null = this.sourceProps?.data as FeatureCollection ?? null;
            clusterDataDidChange = false;
            if(!!currentClusterData !== haveMarkers){
                clusterDataDidChange = true;
            } else if(currentClusterData) {
                if(currentClusterData.features.length !== features.length){
                    clusterDataDidChange = true;
                } else {
                    for(let feature of features){
                        let currentFeature:Feature|null = null;

                        for(let checkFeature of currentClusterData.features){
                            if((checkFeature.properties?.markerID ?? "") === (feature.properties?.markerID ?? "") ){
                                currentFeature = checkFeature;
                            }
                        }
                        if(!currentFeature){
                            clusterDataDidChange = true;
                            break;
                        }
                        const childCount:number = feature.properties?.childCount ?? 0;
                        const currentChildCount:number = currentFeature.properties?.childCount ?? 0;
                        if(childCount !== currentChildCount){
                            break;
                        }
                        const point = feature.geometry as Point;
                        const latitude:number|null = point.coordinates[1] ?? null;
                        const longitude:number|null = point.coordinates[0] ?? null;

                        const currentPoint = currentFeature.geometry as Point;
                        const currentLatitude:number|null = currentPoint.coordinates[1] ?? null;
                        const currentLongitude:number|null = currentPoint.coordinates[0] ?? null;

                        if((latitude !== currentLatitude) || (longitude !== currentLongitude)){
                            clusterDataDidChange = true;
                            break;
                        }
                        const refreshIndex:number = feature.properties?.refreshIndex ?? 0;
                        const currentRefreshIndex:number = currentFeature.properties?.refreshIndex ?? 0;
                        if(refreshIndex !== currentRefreshIndex){
                            clusterDataDidChange = true;
                            break;
                        }
                    }
                }
            }
        }

        for(let markerID in markersProps){
            markersProps[markerID].markerID = markerID;
            const marker = markersProps[markerID];
            deepMergeObjects(marker, nextProps.marker)
        }

        this.markersProps = markersProps;

        if(sourcePropsDidChange || clusterDataDidChange)
        {
            const configProps = mapCoreConfig.cluster ?? null;

            this.sourceID = nextProps.source?.id ?? (configProps?.layer?.source ?? 'none');

            const clusterData:FeatureCollection = {
                type: "FeatureCollection",
                features: features
            }


            const layerProps:CircleLayerSpecification = {
                ...configProps?.layer,
                ...nextProps.layer,
                id: nextProps.layer?.id ?? (configProps?.layer?.id ?? 'none'),
                type: 'circle',
                source: this.sourceID,
                paint: {
                    ...configProps?.layer?.paint,
                    ...nextProps.layer?.paint,
                    'circle-opacity': 0
                }
            }

            this.sourceProps = {
                ...configProps?.source,
                ...nextProps.source,
                id: this.sourceID,
                type: nextProps.source?.type ?? (configProps?.source?.type ?? "geojson"),
                data: clusterData,
                cluster: nextProps.source?.cluster ?? configProps?.source?.cluster,
                clusterMaxZoom: nextProps.source?.clusterMaxZoom ?? configProps?.source?.clusterMaxZoom,
                clusterRadius: nextProps.source?.clusterRadius ?? configProps?.source?.clusterRadius,
                clusterProperties: {
                    ...configProps?.source?.clusterProperties as IAnyObject,
                    ...nextProps.source?.clusterProperties as IAnyObject,
                    itemCount: ["+", ["get", "childCount"]],
                    minLatitude: ["min", ["get", "latitude"]],
                    maxLatitude: ["max", ["get", "latitude"]],
                    minLongitude: ["min", ["get", "longitude"]],
                    maxLongitude: ["max", ["get", "longitude"]]
                },
                children: <Layer
                    {...layerProps}
                />
            }
        }
        let wasForceRemoveClusters = this.forceRemoveClusters;
        this.forceRemoveClusters = !features.length
        let forceRemoveClustersDidChange = this.forceRemoveClusters !== wasForceRemoveClusters;

        if(clusterIconPropsDidChange){
            this.clusterIconPropsDidChange = true;
        }

        if(forceRemoveClustersDidChange){
            this.waitingForSourceData = false;
        } else if(clusterDataDidChange){
            this.waitingForSourceData = true;
        }

        if(!this.waitingForSourceData){

            this.stateEstablishedPromises = this.stateEstablishedPromises.concat(this.nextUpdatePromises);
            this.nextUpdatePromises.length = 0;
            if(clusterIconPropsDidChange || markersPropsDidChange || this.updateIsRequired || forceRemoveClustersDidChange){
                this.updateClusters()
                this.updatePopup();
            }
        }
        this.updateIsRequired = false;
    }

    updatePopup(){
        const popup = this.props.map.getPopup();
        if(popup && popup.mounted){
            popup.previousPopupMarker = null;
            popup.refreshState()
        }
    }

    createMarkerPropsList(markersProps:IMarkersProps|null, sortByNorthWest:boolean = false): IMarkerProps[]
    {
        markersProps = markersProps ?? this.markersProps;
        const markerPropsList:IMarkerProps[] = [];
        let bounds:LngLatBounds|null = null;
        for(let markerID in markersProps){
            const props = markersProps[markerID];
            props.markerID = markerID;
            markerPropsList.push(markersProps[markerID]);
            if(sortByNorthWest){
                if(!bounds){
                    bounds = new LngLatBounds([props.marker.longitude, props.marker.latitude, props.marker.longitude, props.marker.latitude])
                } else {
                    bounds.extend([props.marker.longitude, props.marker.latitude])
                }
            }
        }

        if(bounds)
        {
            const northWest = bounds.getNorthWest();
            const distances:INumberObject = {};
            for(let props of markerPropsList){
                distances[props.markerID!] = getManager().location.getDistanceInMetresBetween(new LngLat(props.marker.longitude, props.marker.latitude), northWest);
            }

            markerPropsList.sort((a, b) => {
                const aDistance = distances[a.markerID!] ?? null;
                const bDistance = distances[b.markerID!] ?? null;
                if(aDistance !== null && bDistance === null){
                    return  -1;
                }
                if(aDistance === null && bDistance !== null){
                    return  1;
                }
                if(aDistance !== null){
                    if(aDistance < bDistance){
                        return  -1;
                    }
                    if(aDistance > bDistance){
                        return  1;
                    }
                }
                return 0;
            });
        }
        return markerPropsList;
    }

    createClusterFeatures(markerPropsList:IMarkerProps[], excludedMarkerIDs:string[] = [], excludedObjectIDs:IStringArrayObject = {}): Feature[]
    {

        const features:Feature[] = [];
        for(let props of markerPropsList){
            if(excludedMarkerIDs.includes(props.markerID!)){
                continue;
            }
            const markerExcludedObjectIDs = excludedObjectIDs[props.markerID!] ?? []
            const objectIDs = (props.objectIDs ?? []).slice();
            if(objectIDs.length){
                for(let objectID of markerExcludedObjectIDs){
                    let index = objectIDs.indexOf(objectID);
                    if(index !== -1){
                        objectIDs.splice(index, 1);
                    }
                }
                if(!objectIDs.length){
                    continue;
                }
            }
            features.push({
                type: "Feature",
                properties: {
                    markerID: props.markerID!,
                    childCount: objectIDs.length ? objectIDs.length : 1,
                    latitude: props.marker.latitude,
                    longitude: props.marker.longitude,
                    refreshIndex: this.refreshIndex

                },
                geometry: {
                    type: "Point",
                    coordinates: [
                        props.marker.longitude,
                        props.marker.latitude
                    ]
                }
            })
        }
        return features;
    }

    checkForTransition(nextProps:IProps, props?:IProps){
        const transitionOnClusterChange = this.props.transitionOnClusterChange ?? (mapCoreConfig.cluster?.transitionOnClusterChange ?? false);
        if(!transitionOnClusterChange){
            return;
        }

        if(!this.appliedToMap || !this.props.map.isMapReady){
            return;
        }
        const nextMarkersProps = nextProps.markers ?? {};
        const currentMarkersProps = props?.markers ?? {};
        if(Object.keys(nextMarkersProps).length === Object.keys(currentMarkersProps).length){
            let match = true;
            for(let markerID in nextMarkersProps){
                const nextMarkerProps = nextMarkersProps[markerID];
                const currentMarkerProps:IMarkerProps|null = currentMarkersProps[markerID] ?? null;
                if(!currentMarkerProps){
                    match = false;
                } else {
                    if((nextMarkerProps.marker.latitude !== currentMarkerProps.marker.latitude) || (nextMarkerProps.marker.longitude !== currentMarkerProps.marker.longitude)){
                        match = false;
                    }
                }
            }
            if(match){
                return;
            }
        }
        this.props.map.showTransition();
    }

    updateClusters():void
    {
        if(this.waitingForSourceData || !this.appliedToMap){
            return;
        }

        const map = this.props.map;
        const mapMap = map.mounted ? map.getMap() : null;
        if(!mapMap){
            return;
        }

        if (!mapMap.getSource(this.sourceID) || !mapMap.isSourceLoaded(this.sourceID))
        {
            return;
        }
        const features = mapMap.querySourceFeatures(this.sourceID);

        const activeClusterIDs:string[] = [];

        const requireActiveClusterObject = (clusterID:string, feature:Feature|null, latitude:number, longitude:number, markerID:string|null = null, itemCount = 1, objectID:string|null = null) => {

            if(activeClusterIDs.includes(clusterID)){
                return;
            }
            activeClusterIDs.push(clusterID);

            const getFromMarkerProps = () => {
                return markerID && this.props.markers ? (this.props.markers[markerID] ?? null) : null;
            }

            let clusterObject:IClusterObject|null = this.clusterObjects[clusterID] ?? null;

            const useClusterIcon = !markerID || (itemCount !== 1);

            const fromMarkerProps = getFromMarkerProps();

            const getObjectIDs = (includeRelated:boolean = false) => {
                const fromObjectIDs = fromMarkerProps?.objectIDs ?? [];
                return includeRelated || !objectID || !fromObjectIDs.includes(objectID) ? fromObjectIDs : [objectID]
            }

            if(clusterObject){
                if(
                    ((itemCount === 1) !== (clusterObject.itemCount === 1)) ||
                    (fromMarkerProps !== clusterObject?.fromMarkerProps) ||
                    (useClusterIcon && this.clusterIconPropsDidChange)
                ){
                    clusterObject = null;
                }
            }

            const getOnClick = ():((e: MarkerEvent<MouseEvent>) => void) => {
                if(!markerID){
                    return (e: MarkerEvent<MouseEvent>) => {
                        if(!map.mounted){
                            return;
                        }
                        const clusterMarker = clusterObject?.mapMarkerRef.current ?? null;
                        const north:number|null = feature?.properties?.maxLatitude ?? null
                        const south:number|null = feature?.properties?.minLatitude ?? null
                        const east:number|null = feature?.properties?.maxLongitude ?? null
                        const west:number|null = feature?.properties?.minLongitude ?? null
                        let zoomLevel:number|null = null;
                        if(north !== null && south !== null && east !== null && west !== null){
                            const lngLatBounds = new LngLatBounds(new LngLat(west, south), new LngLat(east, north));
                            zoomLevel = map.getZoomLevelForBounds(lngLatBounds, true);
                            const currentZoomLevel = map.getMap()?.getZoom() ?? null;
                            if(zoomLevel !== null && currentZoomLevel !== null){
                                zoomLevel --;
                                const clusterMaxZoomLevel = this.sourceProps?.clusterMaxZoom ?? null;
                                if(clusterMaxZoomLevel){
                                    zoomLevel = Math.min(zoomLevel, clusterMaxZoomLevel + 1);
                                }
                                zoomLevel = Math.max(zoomLevel, currentZoomLevel + 1)
                            }
                        }
                        if(!clusterMarker){
                            return;
                        }
                        map.zoomToMarker(clusterMarker, zoomLevel);
                    }
                }
                if(getObjectIDs().length > 1){
                    return (e: MarkerEvent<MouseEvent>) => {
                        if(!map.mounted){
                            return;
                        }
                        const fromMarkerProps = getFromMarkerProps();
                        const markerID = fromMarkerProps?.markerID ?? null;
                        const objectIDs = getObjectIDs();
                        if(markerID && objectIDs.length){
                            let objectID:string|null = null;
                            if(!this.excludeFromClusterMarkerIDs.length){
                                if(Object.keys(this.excludeFromClusterObjectIDs).length === 1){
                                    const excludeObjectIDs = this.excludeFromClusterObjectIDs[markerID] ?? [];
                                    if(excludeObjectIDs.length === 1){
                                        for(let i = 1; i < objectIDs.length; i++){
                                            if(objectIDs[i - 1] === excludeObjectIDs[0]){
                                                objectID = objectIDs[i];
                                            }
                                        }
                                    }
                                }
                            }
                            objectID = objectID ?? objectIDs[0];
                            map.selectClusterMarker(markerID, objectID, !!fromMarkerProps?.centreOnClick, true, true);
                        }
                        if(fromMarkerProps?.marker.onClick){
                            fromMarkerProps.marker.onClick(e);
                        }
                    }
                }
                return (e: MarkerEvent<MouseEvent>) => {
                    const fromMarkerProps = getFromMarkerProps();
                    if(getObjectIDs(true).length < 2){
                        this.excludeMarkerFromCluster(markerID,null, true).then()
                    }
                    if(fromMarkerProps?.marker.onClick){
                        fromMarkerProps.marker.onClick(e);
                    }
                }
            }

            const createMarker = ():MarkerProps => {
                const style: React.CSSProperties = {
                    ...fromMarkerProps?.marker.style,
                }
                return {
                    ...fromMarkerProps?.marker,
                    latitude: latitude,
                    longitude: longitude,
                    style: style,
                    onClick: getOnClick(),
                }
            }

            const createClusterIcon = (ref: RefObject<MapClusterIconInstance|null>, itemCount:number):React.ReactNode => {
                return <MapClusterIcon
                    {...this.props.clusterIcon}
                    ref={ref}
                    itemCount={itemCount}
                />
            }

            if(!clusterObject){

                let markerProps:IMarkerProps;

                const fromConfigMarkerProps = mapCoreConfig.cluster?.marker ?? null;


                let defaultZIndex:Property.ZIndex|undefined;
                if((fromMarkerProps?.defaultZIndex ?? null) !== null){
                    defaultZIndex = fromMarkerProps!.defaultZIndex;
                } else if(!useClusterIcon){
                    defaultZIndex = fromConfigMarkerProps?.defaultZIndex;
                } else if(!!markerID){
                    if((this.props.buttonZIndex ?? null) !== null){
                        defaultZIndex = this.props.buttonZIndex;
                    } else {
                        defaultZIndex = mapCoreConfig.cluster?.buttonZIndex;
                    }
                } else {
                    if(this.props.zIndex){
                        defaultZIndex = this.props.zIndex;
                    } else {
                        defaultZIndex = mapCoreConfig.cluster?.zIndex;
                    }
                }
                let centreOnClick;
                if((fromMarkerProps?.centreOnClick ?? null) !== null){
                    centreOnClick = fromMarkerProps!.centreOnClick;
                } else if(!!markerID){
                    centreOnClick = fromConfigMarkerProps?.centreOnClick ?? false
                } else {
                    centreOnClick = false;
                }

                const objectIDs = getObjectIDs()

                markerProps = {
                    ...fromMarkerProps,
                    marker: createMarker(),
                    popupContent: objectIDs.length < 2 ? fromMarkerProps?.popupContent ?? null : null,
                    markerID: markerID,
                    objectIDs: objectIDs,
                    defaultZIndex: defaultZIndex,
                    centreOnClick: centreOnClick
                };

                if(useClusterIcon)
                {
                    const clusterIconRef = React.createRef<MapClusterIconInstance>();
                    markerProps.iconRef = clusterIconRef;

                    markerProps.icon = createClusterIcon(clusterIconRef, itemCount);
                } else {
                    markerProps.iconProps = {
                        ...fromMarkerProps?.iconProps,
                        color: fromMarkerProps?.iconProps?.color ?? mapCoreConfig.cluster?.marker?.iconProps?.color,
                        innerColor: fromMarkerProps?.iconProps?.innerColor ?? mapCoreConfig.cluster?.marker?.iconProps?.innerColor,
                        strokeColor: fromMarkerProps?.iconProps?.strokeColor ?? mapCoreConfig.cluster?.marker?.iconProps?.strokeColor,
                        imagePath: fromMarkerProps?.iconProps?.imagePath !== undefined ? fromMarkerProps?.iconProps?.imagePath : mapCoreConfig.cluster?.marker?.iconProps?.imagePath
                    }
                }

                clusterObject = {
                    clusterID: clusterID,
                    feature: feature,
                    lngLat: new LngLat(longitude, latitude),
                    mapMarkerRef: React.createRef<MapMarkerInstance>(),
                    fromMarkerProps: fromMarkerProps,
                    markerProps: markerProps,
                    active: true,
                    itemCount: itemCount,
                    markerID: markerID,
                    objectID: objectID
                }
                this.clusterObjects[clusterID] = clusterObject;
            } else {

                clusterObject.active = true;
                if(
                    latitude !== clusterObject.lngLat.lat ||
                    longitude !== clusterObject.lngLat.lng
                ){
                    clusterObject.lngLat = new LngLat(longitude, latitude);
                    clusterObject.markerProps.marker = createMarker();
                }
                if(
                    useClusterIcon && (itemCount !== clusterObject.itemCount) || (useClusterIcon && this.clusterIconPropsDidChange)
                ){
                    clusterObject.itemCount = itemCount;
                    clusterObject.markerProps.icon = createClusterIcon(clusterObject.markerProps.iconRef as RefObject<MapClusterIconInstance|null>, itemCount);
                }
                clusterObject.feature = feature;
            }
        }

        if(!this.forceRemoveClusters){
            for (const feature of features) {
                const props = feature.properties;
                const markerID:string|null = props.markerID ? props.markerID.toString() : null;
                const clusterID = markerID ?? (props.cluster_id ? "cluster_"+props.cluster_id : "");
                const geometry = feature.geometry as Point
                const coords = geometry.coordinates;
                const itemCount:number = parseInt(props.itemCount ?? (props.childCount ?? "1"));
                const latitude:number = coords[1];
                const longitude:number = coords[0];
                requireActiveClusterObject(clusterID, feature, latitude, longitude, markerID, itemCount);
            }
        }

        const excludedFromClusterMarkerProps:IMarkersProps = {};
        const clusterIdObjectIDs:IStringObject = {};
        for(let markerID of this.excludeFromClusterMarkerIDs){
            const markerProps:IMarkerProps|null = this.markersProps[markerID] ?? null;
            if(markerProps){
                excludedFromClusterMarkerProps[markerID] = markerProps;
            }
        }

        for(let markerID in this.excludeFromClusterObjectIDs){
            if(this.excludeFromClusterMarkerIDs.includes(markerID)){
                continue
            }
            const objectIDs = this.excludeFromClusterObjectIDs[markerID]
            const markerProps = this.markersProps[markerID];
            if(objectIDs && markerProps.objectIDs){
                for(let objectID of objectIDs){
                    if(markerProps.objectIDs.includes(objectID)){
                        const clusterID = this.getClusterID(markerID, objectID);
                        clusterIdObjectIDs[clusterID] = objectID;
                        excludedFromClusterMarkerProps[clusterID] = markerProps;
                    }
                }
            }
        }

        for(let clusterID in excludedFromClusterMarkerProps){
            const markerProps= excludedFromClusterMarkerProps[clusterID];
            const markerID = markerProps.markerID ?? "";
            const objectID = clusterIdObjectIDs[clusterID] ?? null;
            requireActiveClusterObject(clusterID, null, markerProps.marker.latitude, markerProps.marker.longitude, markerID, 1, objectID);
        }

        this.clusterIconPropsDidChange = false;
        this.willRenderContent = true;

        for(let clusterID in this.clusterObjects){
            const clusterObject = this.clusterObjects[clusterID];

            if(clusterObject.active && !activeClusterIDs.includes(clusterID)){
                clusterObject.active = false;

                const clusterMarker = clusterObject?.mapMarkerRef.current ?? null;
                if(clusterMarker && (clusterMarker === this.props.map.selectedMarker)){
                    this.props.map.selectMarker(null);
                }

            }
        }
    }

    canCycleClusterMarkers(){
        const choiceList = this.markerPropsList;
        return choiceList.length > 1 || (choiceList.length === 1 && choiceList[0].objectIDs && choiceList[0].objectIDs.length > 1);
    }

    showPreviousClusterMarker(){
        const choiceList:IMarkerProps[] = this.markerPropsList;
        const map = this.props.map;

        let fromMarkerID:string|null;
        if(map.selectedMarker){
            fromMarkerID = map.selectedMarker.markerID;
        } else {
            const fromMarker:IMarkerProps|null = choiceList.length ? choiceList[0] : null;
            fromMarkerID = fromMarker?.markerID ?? null;
        }
        let fromMarker = fromMarkerID ? (this.markersProps[fromMarkerID] ?? null) : null;
        fromMarker = fromMarker ? fromMarker : choiceList[0];
        if(!fromMarker){
            return;
        }
        fromMarkerID = fromMarker.markerID!;
        let fromObjectID:string|null = null;
        const fromObjectIDs = fromMarker.objectIDs ?? [];
        if(fromObjectIDs.length){
            fromObjectID = this.getSelectedObjectID(fromMarkerID) ?? null;
            fromObjectID = fromObjectID && fromObjectIDs.includes(fromObjectID) ? fromObjectID : fromObjectIDs[0];
        }
        if(fromObjectID){
            const index = fromObjectIDs.indexOf(fromObjectID);
            if(index > 0){
                const toObjectID = fromObjectIDs[index - 1];
                map.selectClusterMarker(fromMarkerID, toObjectID, true, true);
                return;
            }
        }
        let fromMarkerIndex = -1;
        for(let i = 0; i < choiceList.length; i ++){
            const checkMarker = choiceList[i];
            if(checkMarker.markerID === fromMarkerID){
                fromMarkerIndex = i;
                break;
            }
        }

        if(fromMarkerIndex < 0){
            fromMarkerIndex = 0;
            if(fromObjectIDs.length){
                fromObjectID = fromObjectIDs[0]
            }
        }
        const toMarker:IMarkerProps = fromMarkerIndex !== 0 ? choiceList[fromMarkerIndex - 1] : choiceList[choiceList.length - 1];
        const toMarkerID = toMarker.markerID!;

        const toObjectIDs = toMarker.objectIDs ?? [];
        const toObjectID:string|null = toObjectIDs.length ? toObjectIDs[toObjectIDs.length - 1] : null;
        if((fromMarkerID === toMarkerID) && (fromObjectID === toObjectID)){
            return;
        }
        map.selectClusterMarker(toMarkerID, toObjectID, true, true);
    }

    showNextClusterMarker(){
        const choiceList:IMarkerProps[] = this.markerPropsList ?? [];
        const map = this.props.map;

        let fromMarkerID:string|null;
        if(map.selectedMarker){
            fromMarkerID = map.selectedMarker.markerID;
        } else {
            const fromMarker:IMarkerProps|null = choiceList.length ? choiceList[0] : null;
            fromMarkerID = fromMarker?.markerID ?? null;
        }
        let fromMarker = fromMarkerID ? this.markersProps[fromMarkerID] : null;
        fromMarker = fromMarker ? fromMarker : choiceList[0];
        if(!fromMarker){
            return;
        }
        fromMarkerID = fromMarker.markerID!;
        let fromObjectID:string|null = null;
        const fromObjectIDs = fromMarker.objectIDs ?? [];
        if(fromObjectIDs.length){
            fromObjectID = this.getSelectedObjectID(fromMarkerID) ?? null;
            fromObjectID = fromObjectID && fromObjectIDs.includes(fromObjectID) ? fromObjectID : fromObjectIDs[0];
        }
        if(fromObjectID){
            const index = fromObjectIDs.indexOf(fromObjectID);
            if(index < fromObjectIDs.length - 1){
                const toObjectID = fromObjectIDs[index + 1];
                map.selectClusterMarker(fromMarkerID, toObjectID, true, true);
                return;
            }
        }
        let fromMarkerIndex = -1;
        for(let i = 0; i < choiceList.length; i ++){
            const checkMarker = choiceList[i];
            if(checkMarker.markerID === fromMarkerID){
                fromMarkerIndex = i;
                break;
            }
        }
        if(fromMarkerIndex < 0){
            fromMarkerIndex = choiceList.length - 1;
            if(fromObjectIDs.length){
                fromObjectID = fromObjectIDs[fromObjectIDs.length - 1]
            }
        }
        const toMarker:IMarkerProps = fromMarkerIndex !== choiceList.length - 1 ? choiceList[fromMarkerIndex + 1] : choiceList[0];
        const toMarkerID = toMarker.markerID!;

        const toObjectIDs = toMarker.objectIDs ?? [];
        const toObjectID:string|null = toObjectIDs.length ? toObjectIDs[0] : null;
        if((fromMarkerID === toMarkerID) && (fromObjectID === toObjectID)){
            return;
        }
        map.selectClusterMarker(toMarkerID, toObjectID, true, true);
    }

    isWaitingForUpdate():boolean
    {
        return !this.appliedToMap || this.waitingForSourceData || this.willRenderContent;
    }

    isStateWaitingForResponse():boolean
    {
        return this.isWaitingForUpdate() || super.isStateWaitingForResponse();
    }

    componentDidFirstMount()
    {
        super.componentDidFirstMount();
        if(this.props.map.initialStateEstablished){
            this.applyToMap();
        }
    }

    onInitialStateEstablished()
    {
        super.onInitialStateEstablished();
        this.props.map.refreshState()
    }

    componentWillUnmount()
    {
        super.componentWillUnmount();
        this.nextUpdatePromises.length = 0;
    }

    onPostRender()
    {
        super.onPostRender();
        this.willRenderContent = false;
    }

    applyToMap()
    {
        if(this.appliedToMap){
            return;
        }

        const map = this.props.map;
        if(!map.mounted || !map.didFindInitialFocus){
            return;
        }

        const mapMap = map.getMap();
        if (!mapMap)
        {
            return;
        }

        mapMap.on("move", (event) =>
        {
            this.updateClusters();
            this.refreshState()
        });
        mapMap.on("moveend", (event) =>
        {
            this.refreshIndex ++;
            this.refreshState()
        });
        mapMap.on("sourcedata", (event) =>
        {
            if(event.isSourceLoaded){
                if(event.sourceId === this.sourceID)
                {
                    this.waitingForSourceData = false;
                    this.updateClusters();
                    this.stateEstablishedPromises = this.stateEstablishedPromises.concat(this.nextUpdatePromises);
                    this.nextUpdatePromises.length = 0;
                    this.refreshState();
                    this.updatePopup();
                }
            }
        });
        this.appliedToMap = true;
        this.refreshState();
    }

    getClusterBounds(userPosition?:LngLat|null, primaryMarkerPosition?:LngLat|null, countryBounds?:LngLatBounds|null, userCountryCode?:string|null): LngLatBounds|null
    {
        const clusterPositions:LngLat[] = [];
        for(let markerID in this.markersProps){
            const markerProps = this.markersProps[markerID];
            if(markerProps.outOfBounds){
                continue;
            }
            clusterPositions.push(new LngLat(markerProps.marker.longitude, markerProps.marker.latitude))
        }
        const withUser = this.props.withUser ?? (mapCoreConfig.cluster?.withUser ?? false);
        const withPrimaryMarker = this.props.withPrimaryMarker ?? (mapCoreConfig.cluster?.withPrimaryMarker ?? false);
        const withCountry = this.props.withCountry ?? (mapCoreConfig.cluster?.withCountry ?? false);

        if(withUser && userPosition){
            if(withUser === true || withUser === userCountryCode){
                clusterPositions.push(userPosition)
            }
        }
        if(withPrimaryMarker && primaryMarkerPosition)
        {
            clusterPositions.push(primaryMarkerPosition)
        }
        if(withCountry && countryBounds){
            for(let i = 0; i < clusterPositions.length; i++ ){
                const position = clusterPositions[i];
                if(!countryBounds.intersects(new LngLatBounds(position, position))){
                    clusterPositions.splice(i, 1);
                    i --;
                }
            }
        }
        if(!clusterPositions.length){
            return null;
        }
        const bounds = new LngLatBounds(clusterPositions[0], clusterPositions[0]);
        for(let i = 1; i < clusterPositions.length; i++ ){
            bounds.extend(clusterPositions[i])
        }

        return bounds;
    }

    getActiveFocusZoomLevel(focusZoomLevel:number, primaryMarkerZoomLevel?:number|null, countryZoomLevel?:number|null):number
    {
        const clusterFocusZoomLevel = focusZoomLevel > 2 ? focusZoomLevel - 1 : focusZoomLevel;

        if(!!this.props.withPrimaryMarker){
            if(primaryMarkerZoomLevel)
            {
                return primaryMarkerZoomLevel;
            }
        }
        if(!!this.props.withCountry){
            if(countryZoomLevel)
            {
                if(clusterFocusZoomLevel <= countryZoomLevel){
                    return countryZoomLevel;
                }
                return Math.min(countryZoomLevel + 4, clusterFocusZoomLevel);
            }
        }
        return clusterFocusZoomLevel
    }

    containsFocus(focus:MapFocus):boolean
    {
        if(focus === "primary_marker" && (this.props.withPrimaryMarker ?? false) ){
            return true;
        }
        if(focus === "user" && (this.props.withUser ?? false) ){
            return true;
        }
        return focus === "country" && (this.props.withCountry ?? false);

    }


    getSelectedObjectID(forMarkerID?:string|null){
        if(!forMarkerID){
            const keys = Object.keys(this.excludeFromClusterObjectIDs);
            forMarkerID = keys.length === 1 ? keys[0] : null;
        }
        const objectIDs = forMarkerID ? (this.excludeFromClusterObjectIDs[forMarkerID] ?? []) : [];
        return objectIDs.length === 1 ? objectIDs[0] : null;
    }

    async excludeMarkerFromCluster(markerID:string, objectID:string|null = null, ifNotActive:boolean = false, addToExclusion:boolean = false): Promise<MapMarkerInstance|null>
    {

        if(ifNotActive){
            const clusterObject = markerID ? this.clusterObjects[markerID] : null
            const clusterMarker = clusterObject?.mapMarkerRef.current ?? null;
            if(clusterMarker && clusterObject?.active){
                return clusterMarker;
            }
        }

        const markerProps = this.markersProps[markerID];
        if(objectID){
            const objectIDs = markerProps.objectIDs ?? [];
            if((objectIDs.length === 1) && (objectIDs[0] === objectID)){
                objectID = null;
            }
        }
        if(!markerProps || (objectID && !markerProps.objectIDs?.includes(objectID)) || !this.sourceProps){
            return null;
        }

        const getMarker = (requireSingleExclusion = false) => {
            const clusterID = this.getClusterID(markerID, objectID);
            const clusterObject = this.clusterObjects[clusterID];
            if(clusterObject){
                let valid = false;
                if(objectID){
                    const markerExcludedObjectIDs = this.excludeFromClusterObjectIDs[markerID] ?? []
                    if(markerExcludedObjectIDs.includes(objectID)){
                        if(!requireSingleExclusion || ((markerExcludedObjectIDs.length === 1) && !this.excludeFromClusterMarkerIDs.length)){
                            valid = true;
                        }
                    }
                } else {
                    if(this.excludeFromClusterMarkerIDs.includes(markerID)){
                        if(!requireSingleExclusion || ((this.excludeFromClusterMarkerIDs.length === 1) && !Object.keys(this.excludeFromClusterObjectIDs).length)){
                            valid = true;
                        }
                    }
                }
                if(valid){
                    return clusterObject.mapMarkerRef.current ?? null;
                }
            }
            return null;
        }
        const marker = getMarker(!addToExclusion);
        if( marker ){
            return marker;
        }
        if(!addToExclusion){
            this.excludeFromClusterMarkerIDs.length = 0;
            this.excludeFromClusterObjectIDs = {};
        }
        if(!objectID){
            if(!this.excludeFromClusterMarkerIDs.includes(markerID)){
                this.excludeFromClusterMarkerIDs.push(markerID);
            }
        } else {

            let markerIndex = this.excludeFromClusterMarkerIDs.indexOf(markerID);
            if(markerIndex !== -1){
                this.excludeFromClusterMarkerIDs.splice(markerIndex, 1);
            }

            if(!this.excludeFromClusterObjectIDs[markerID]){
                this.excludeFromClusterObjectIDs[markerID] = [];
            }
            if(!this.excludeFromClusterObjectIDs[markerID].includes(objectID)){
                this.excludeFromClusterObjectIDs[markerID].push(objectID);
            }
        }

        const promise:DeferredPromise<void, void> = createDeferredPromise<void, void>();
        this.nextUpdatePromises.push(promise);
        this.updateIsRequired = true;

        this.refreshState();
        return promise.then(() =>{
            return getMarker();
        });
    }

    getClusterID(markerID:string, objectID:string|null = null){
        return !objectID ? markerID : markerID + "_object_" + objectID;
    }

    isClusterMarker(markerID:string|null, andActive:boolean = false): boolean
    {
        const clusterObject = markerID ? this.clusterObjects[markerID] : null
        return !!clusterObject && (!andActive || clusterObject.active);
    }

    async deselectMarker(marker:MapMarkerInstance): Promise<void>
    {
        let didDeselect = false;
        if(marker.props.markerID && this.props.markers && this.props.markers[marker.props.markerID]){
            let markerIndex = this.excludeFromClusterMarkerIDs.indexOf(marker.props.markerID);
            if(markerIndex !== -1){
                this.excludeFromClusterMarkerIDs.splice(markerIndex, 1);
                didDeselect = true;
            }
            if(marker.props.objectIDs?.length === 1){
                const objectID = marker.props.objectIDs[0];
                const markerExcludedObjectIDs = this.excludeFromClusterObjectIDs[marker.props.markerID] ?? []
                let objectIndex = markerExcludedObjectIDs.indexOf(objectID);
                if(objectIndex !== -1){
                    markerExcludedObjectIDs.splice(objectIndex, 1);
                    if(!markerExcludedObjectIDs.length){
                        delete this.excludeFromClusterObjectIDs[marker.props.markerID]
                    }
                    didDeselect = true;
                }
            }
        }
        if(!didDeselect){
            return;
        }
        this.updateIsRequired = true;

        const promise:DeferredPromise<void, void> = createDeferredPromise<void, void>();
        this.nextUpdatePromises.push(promise);

        this.refreshState();
        return promise;
    }

    renderComponent()
    {
        if(!this.appliedToMap){
            return <></>
        }

        const featuresLength = (this.sourceProps?.data as FeatureCollection)?.features.length ?? 0;

        return <>
            {!!this.sourceProps && !!featuresLength && <>
                <Source
                    {...this.sourceProps}
                >
                </Source>
            </>}

            {Object.keys(this.clusterObjects).map((clusterID, index) =>
            {
                const clusterObject = this.clusterObjects[clusterID];
                if(!clusterObject.active){
                    return "";
                }
                return <MapMarker
                    {...clusterObject.markerProps}
                    key={clusterID}
                    ref={clusterObject.mapMarkerRef}
                    map={this.props.map}
                    markerType={"cluster"}
                />
            })}
        </>;
    }
}
const MapCluster  = React.memo(MapClusterInstance);
export default MapCluster;